import React, { useMemo } from 'react';
import ChatMessage from './ChatMessage';
import StandardQuestions from './StandardQuestions';
import ThinkingBlock from './ThinkingBlock';
import ToolExecution from './ToolExecution';
import AgentProgress from './AgentProgress';
import IconSvg from '../../../../assets/icons/Icon.svg';

// Animated typing indicator component
const TypingIndicator = () => {
    // Handle both string URL and object exports from SVG imports
    const logoSrc = typeof IconSvg === 'string' ? IconSvg : (IconSvg?.default || IconSvg);

    return (
        <div className="ntb-chat-message assistant ntb-typing-message">
            <div className="ntb-message-avatar ntb-avatar-pulse">
                <img
                    src={logoSrc}
                    alt="NanoTrader Copilot"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                    }}
                />
            </div>
            <div className="ntb-message-body">
                <div className="ntb-typing-indicator">
                    <span className="ntb-typing-dot"></span>
                    <span className="ntb-typing-dot"></span>
                    <span className="ntb-typing-dot"></span>
                </div>
            </div>
        </div>
    );
};

/**
 * ChatMessages component for displaying the chat messages
 */
const ChatMessages = ({
    messages,
    isChatLoading,
    selectedInstrument,
    selectedCategory,
    selectedChatId,
    onQuestionClick,
    addLine,
    chatWindowRef,
    thinkingState,
    toolExecutions,
    agentSteps
}) => {
    // Convert toolExecutions object to array for rendering
    const toolExecutionsList = useMemo(() => {
        if (!toolExecutions) return [];
        return Object.values(toolExecutions);
    }, [toolExecutions]);

    // Check if thinking, tools, or agent steps should be displayed
    const showThinking = thinkingState && (thinkingState.isThinking || thinkingState.content);
    const showTools = toolExecutionsList.length > 0;
    const showAgentProgress = agentSteps && agentSteps.length > 0;

    return (
        <div className="ntb-chat-messages" ref={chatWindowRef}>
            {messages.length === 0 && selectedInstrument ? (
                <StandardQuestions
                    onQuestionClick={onQuestionClick}
                    selectedCategory={selectedCategory}
                    selectedInstrument={selectedInstrument}
                />
            ) : (
                messages.map((message, index) => (
                    <ChatMessage
                        key={index}
                        message={message}
                        index={index}
                        addLine={addLine}
                        chatId={message.chat_id || message.chatId || selectedChatId}
                        instrument={message.instrument || selectedInstrument}
                        category={message.category || selectedCategory}
                    />
                ))
            )}

            {/* Thinking/Reasoning Block */}
            {showThinking && (
                <div className="ntb-agent-blocks">
                    <ThinkingBlock
                        thinkingId={thinkingState.thinkingId}
                        content={thinkingState.content}
                        isThinking={thinkingState.isThinking}
                        durationMs={thinkingState.durationMs}
                        summary={thinkingState.summary}
                    />
                </div>
            )}

            {/* Tool Execution Blocks */}
            {showTools && (
                <div className="ntb-agent-blocks">
                    {toolExecutionsList.map((tool) => (
                        <ToolExecution
                            key={tool.toolId}
                            toolId={tool.toolId}
                            toolName={tool.toolName}
                            status={tool.status}
                            arguments={tool.arguments}
                            result={tool.result}
                            durationMs={tool.durationMs}
                        />
                    ))}
                </div>
            )}

            {/* Agent Progress for Multi-Step Tasks */}
            {showAgentProgress && (
                <div className="ntb-agent-blocks">
                    <AgentProgress
                        steps={agentSteps}
                        title="Processing your request"
                    />
                </div>
            )}

            {isChatLoading && <TypingIndicator />}
            <div className="ntb-chat-disclaimer-inline">
                AI may make mistakes. Verify important trading information.
            </div>
        </div>
    );
};

export default ChatMessages; 
